import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  iconCheckSmallStep3Courses,
  iconCheckWhiteModalCourses,
  iconCloseModalCourses,
  iconQuizSmallModalCourses,
  iconSearchStep3Courses,
} from '../../assets';
import { useDebounce } from '../../hooks/useDebounce';
import { useTheme } from '../../theme';

/** Contrato da API de listagem para o modal (shared não depende do core). */
export interface FetchQuestionsForQuizParams {
  page: number;
  limit: number;
  includeInactive?: boolean;
  title?: string;
}

export interface FetchQuestionsForQuizItem {
  id: string;
  question?: string;
  difficulty?: string;
  category?: string;
}

export interface FetchQuestionsForQuizResponse {
  data: FetchQuestionsForQuizItem[];
  total: number;
}

export type FetchQuestionsForQuizFn = (
  params: FetchQuestionsForQuizParams
) => Promise<FetchQuestionsForQuizResponse>;

export interface BankQuestion {
  id: string;
  code: string;
  text: string;
  difficulty: 'easy' | 'medium' | 'hard';
  category: string;
}

export interface QuizLessonConfig {
  manualQuestionSelection: boolean;
  easyCount: number;
  mediumCount: number;
  hardCount: number;
  isFinalExam: boolean;
  /** IDs das perguntas selecionadas quando manualQuestionSelection é true */
  selectedQuestionIds?: string[];
}

const defaultConfig: QuizLessonConfig = {
  manualQuestionSelection: false,
  easyCount: 0,
  mediumCount: 0,
  hardCount: 0,
  isFinalExam: false,
};

const MOCK_BANK_QUESTIONS: BankQuestion[] = [
  {
    id: 'q-mock-1',
    code: 'Q001',
    text: 'What is the primary mechanism of action of FerAppease?',
    difficulty: 'easy',
    category: 'Product Knowledge',
  },
  {
    id: 'q-mock-2',
    code: 'Q008',
    text: 'What is the recommended dosage for a medium-sized dog?',
    difficulty: 'medium',
    category: 'Product Knowledge',
  },
  {
    id: 'q-mock-3',
    code: 'Q015',
    text: 'Which species can use FerAppease safely?',
    difficulty: 'easy',
    category: 'Safety',
  },
  {
    id: 'q-mock-4',
    code: 'Q022',
    text: 'How should FerAppease be stored after opening?',
    difficulty: 'hard',
    category: 'Storage & Handling',
  },
  {
    id: 'q-mock-5',
    code: 'Q033',
    text: 'What is the difference between FerAppease and standard supplements?',
    difficulty: 'medium',
    category: 'Product Knowledge',
  },
];

export interface QuizLessonModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (config: QuizLessonConfig) => void;
  initialConfig?: QuizLessonConfig | null;
  /** Perguntas do banco para seleção manual (quem abre o modal busca e passa) */
  availableQuestions?: BankQuestion[];
  /** Se fornecido, lista vem da API paginada com busca; caso contrário usa availableQuestions ou mock */
  fetchQuestions?: FetchQuestionsForQuizFn;
}

function clampNum(value: number): number {
  return Math.max(0, Math.floor(Number(value)) || 0);
}

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 400;

function normalizeDifficulty(apiDifficulty: string | undefined): 'easy' | 'medium' | 'hard' {
  const d = (apiDifficulty ?? '').toLowerCase();
  if (d === 'low') return 'easy';
  if (d === 'medium') return 'medium';
  if (d === 'high') return 'hard';
  return 'medium';
}

function mapApiItemToBankQuestion(
  item: FetchQuestionsForQuizItem,
  indexInPage: number
): BankQuestion {
  return {
    id: item.id,
    code: item.id.slice(0, 8) || `Q${indexInPage + 1}`,
    text: item.question ?? '',
    difficulty: normalizeDifficulty(item.difficulty),
    category: item.category ?? '',
  };
}

export function QuizLessonModal({
  open,
  onClose,
  onConfirm,
  initialConfig = null,
  availableQuestions = [],
  fetchQuestions: fetchQuestionsProp,
}: QuizLessonModalProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [manualQuestionSelection, setManualQuestionSelection] = useState(
    initialConfig?.manualQuestionSelection ??
      defaultConfig.manualQuestionSelection
  );
  const [easyCount, setEasyCount] = useState(
    initialConfig?.easyCount ?? defaultConfig.easyCount
  );
  const [mediumCount, setMediumCount] = useState(
    initialConfig?.mediumCount ?? defaultConfig.mediumCount
  );
  const [hardCount, setHardCount] = useState(
    initialConfig?.hardCount ?? defaultConfig.hardCount
  );
  const [isFinalExam, setIsFinalExam] = useState(
    initialConfig?.isFinalExam ?? defaultConfig.isFinalExam
  );
  const [questionSearch, setQuestionSearch] = useState('');
  const [selectedQuestionIds, setSelectedQuestionIds] = useState<string[]>(
    initialConfig?.selectedQuestionIds ?? []
  );
  const [selectedQuestionDetails, setSelectedQuestionDetails] = useState<
    Record<string, BankQuestion>
  >({});
  const selectedQuestionIdsRef = useRef(selectedQuestionIds);
  selectedQuestionIdsRef.current = selectedQuestionIds;

  const useApi = Boolean(fetchQuestionsProp && manualQuestionSelection);
  const debouncedSearch = useDebounce(questionSearch, SEARCH_DEBOUNCE_MS);

  const [apiPageIndex, setApiPageIndex] = useState(0);
  const [apiTotal, setApiTotal] = useState(0);
  const [apiLoading, setApiLoading] = useState(false);
  const [apiQuestions, setApiQuestions] = useState<BankQuestion[]>([]);

  const questionsForBank = useMemo(
    () =>
      availableQuestions?.length ? availableQuestions : MOCK_BANK_QUESTIONS,
    [availableQuestions]
  );

  const loadQuestions = useCallback(async () => {
    if (!fetchQuestionsProp) return;
    setApiLoading(true);
    try {
      const res = await fetchQuestionsProp({
        page: apiPageIndex + 1,
        limit: PAGE_SIZE,
        includeInactive: false,
        title: debouncedSearch.trim() || undefined,
      });
      const mapped = (res.data ?? []).map((item, i) =>
        mapApiItemToBankQuestion(item, apiPageIndex * PAGE_SIZE + i)
      );
      setApiQuestions(mapped);
      setApiTotal(res.total ?? 0);
      setSelectedQuestionDetails(prev => {
        const next = { ...prev };
        const ids = selectedQuestionIdsRef.current;
        mapped.forEach(q => {
          if (ids.includes(q.id)) next[q.id] = q;
        });
        return next;
      });
    } catch {
      setApiQuestions([]);
      setApiTotal(0);
    } finally {
      setApiLoading(false);
    }
  }, [fetchQuestionsProp, apiPageIndex, debouncedSearch]);

  useEffect(() => {
    if (useApi && open) {
      loadQuestions();
    }
  }, [useApi, open, loadQuestions]);

  useEffect(() => {
    setApiPageIndex(0);
  }, [debouncedSearch]);

  useEffect(() => {
    if (open) {
      setManualQuestionSelection(
        initialConfig?.manualQuestionSelection ??
          defaultConfig.manualQuestionSelection
      );
      setEasyCount(initialConfig?.easyCount ?? defaultConfig.easyCount);
      setMediumCount(initialConfig?.mediumCount ?? defaultConfig.mediumCount);
      setHardCount(initialConfig?.hardCount ?? defaultConfig.hardCount);
      setIsFinalExam(initialConfig?.isFinalExam ?? defaultConfig.isFinalExam);
      setSelectedQuestionIds(initialConfig?.selectedQuestionIds ?? []);
      setSelectedQuestionDetails({});
      setApiPageIndex(0);
      setQuestionSearch('');
    }
  }, [open, initialConfig]);

  const filteredAvailableQuestions = useMemo(() => {
    if (useApi) return apiQuestions;
    if (!questionSearch.trim()) return questionsForBank;
    const q = questionSearch.toLowerCase();
    return questionsForBank.filter(
      quest =>
        quest.text.toLowerCase().includes(q) ||
        quest.code.toLowerCase().includes(q) ||
        quest.category.toLowerCase().includes(q)
    );
  }, [useApi, apiQuestions, questionsForBank, questionSearch]);

  const selectedQuestions = useMemo(() => {
    if (useApi) {
      return selectedQuestionIds
        .map(id => selectedQuestionDetails[id])
        .filter((q): q is BankQuestion => Boolean(q));
    }
    return questionsForBank.filter(q => selectedQuestionIds.includes(q.id));
  }, [
    useApi,
    selectedQuestionIds,
    selectedQuestionDetails,
    questionsForBank,
  ]);

  /** Com Manual Question Selection ativo, os counts do Recipe Builder refletem a seleção. */
  useEffect(() => {
    if (!manualQuestionSelection) return;
    if (selectedQuestionIds.length === 0) {
      setEasyCount(0);
      setMediumCount(0);
      setHardCount(0);
    } else if (selectedQuestions.length > 0) {
      const easy = selectedQuestions.filter(q => q.difficulty === 'easy').length;
      const medium = selectedQuestions.filter(q => q.difficulty === 'medium').length;
      const hard = selectedQuestions.filter(q => q.difficulty === 'hard').length;
      setEasyCount(easy);
      setMediumCount(medium);
      setHardCount(hard);
    }
  }, [manualQuestionSelection, selectedQuestionIds.length, selectedQuestions]);

  const toggleQuestion = useCallback(
    (quest: BankQuestion) => {
      const id = quest.id;
      const currentlySelected = selectedQuestionIds.includes(id);
      if (currentlySelected) {
        setSelectedQuestionIds(prev => prev.filter(i => i !== id));
        setSelectedQuestionDetails(prev => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
      } else {
        setSelectedQuestionIds(prev => [...prev, id]);
        setSelectedQuestionDetails(prev => ({ ...prev, [id]: quest }));
      }
    },
    [selectedQuestionIds]
  );

  const removeSelected = useCallback((id: string) => {
    setSelectedQuestionIds(prev => prev.filter(i => i !== id));
    setSelectedQuestionDetails(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const clearAllSelected = useCallback(() => {
    setSelectedQuestionIds([]);
    setSelectedQuestionDetails({});
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose]
  );

  const total = easyCount + mediumCount + hardCount;

  const handleConfirm = useCallback(() => {
    onConfirm({
      manualQuestionSelection,
      easyCount: clampNum(easyCount),
      mediumCount: clampNum(mediumCount),
      hardCount: clampNum(hardCount),
      isFinalExam,
      ...(manualQuestionSelection && { selectedQuestionIds }),
    });
    onClose();
  }, [
    manualQuestionSelection,
    easyCount,
    mediumCount,
    hardCount,
    isFinalExam,
    selectedQuestionIds,
    onConfirm,
    onClose,
  ]);

  if (!open) return null;

  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    backgroundColor: theme.palette.createCourse.modalOverlay,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: theme.spacing.lg,
  };

  const modalStyle: React.CSSProperties = {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius.lg,
    boxShadow: theme.palette.createCourse.modalShadow,
    width: '100%',
    maxWidth: 480,
    maxHeight: '90vh',
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing.lg,
    paddingBottom: 0,
    flexShrink: 0,
  };

  const headerLeftStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.sm,
  };

  const titleStyle: React.CSSProperties = {
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.xl,
    fontWeight: theme.typography.fontWeight.bold,
    color: theme.palette.text.primary,
    margin: 0,
  };

  const closeButtonStyle: React.CSSProperties = {
    background: 'none',
    border: 'none',
    padding: theme.spacing.xs,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: theme.shape.borderRadius.sm,
    lineHeight: 1,
  };

  const bodyStyle: React.CSSProperties = {
    padding: theme.spacing.lg,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.md,
  };

  const blockBase: React.CSSProperties = {
    padding: theme.spacing.md,
    borderRadius: theme.shape.borderRadius.sm,
    border: `1px solid`,
  };

  const qm = theme.palette.createCourse.quizModal;
  const manualBlockStyle: React.CSSProperties = {
    ...blockBase,
    backgroundColor: qm.manualBlockBg,
    borderColor: qm.manualBlockBorder,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing.md,
    minHeight: 56,
  };

  const manualBlockTextWrapperStyle: React.CSSProperties = {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: 2,
  };

  const rowLabelStyle: React.CSSProperties = {
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.base,
    fontWeight: theme.typography.fontWeight.semibold,
    color: theme.palette.text.primary,
    margin: 0,
  };

  const rowSubtitleStyle: React.CSSProperties = {
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.xs,
    color: theme.palette.text.secondary,
    margin: 0,
    marginTop: 2,
  };

  const toggleTrackStyle: React.CSSProperties = {
    width: 44,
    height: 24,
    borderRadius: 12,
    backgroundColor: manualQuestionSelection
      ? qm.totalBlockBg
      : theme.palette.border.main,
    position: 'relative' as const,
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    flexShrink: 0,
    border: 'none',
    padding: 0,
    display: 'flex',
    alignItems: 'center',
  };

  const toggleThumbStyle: React.CSSProperties = {
    width: 20,
    height: 20,
    borderRadius: '50%',
    backgroundColor: theme.palette.background.paper,
    position: 'absolute' as const,
    top: 2,
    left: manualQuestionSelection ? 22 : 2,
    transition: 'left 0.2s',
    boxShadow: qm.toggleThumbShadow,
  };

  const sectionTitleStyle: React.CSSProperties = {
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.base,
    fontWeight: theme.typography.fontWeight.bold,
    color: theme.palette.text.primary,
    margin: 0,
    marginBottom: theme.spacing.sm,
  };

  const difficultyRowStyle = (borderColor: string): React.CSSProperties => ({
    ...blockBase,
    backgroundColor: theme.palette.background.paper,
    borderColor,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.sm,
    padding: theme.spacing.sm,
  });

  const circleStyle = (
    bg: string,
    textColor?: string
  ): React.CSSProperties => ({
    width: 28,
    height: 28,
    borderRadius: '50%',
    backgroundColor: bg,
    color: textColor ?? theme.palette.text.white,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.sm,
    fontWeight: theme.typography.fontWeight.bold,
    flexShrink: 0,
  });

  const numInputStyle: React.CSSProperties = {
    width: 56,
    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.base,
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.input.background,
    border: `1px solid ${theme.palette.border.main}`,
    borderRadius: theme.shape.borderRadius.sm,
    boxSizing: 'border-box',
    outline: 'none',
    marginLeft: 'auto',
    textAlign: 'right',
  };

  const totalBlockStyle: React.CSSProperties = {
    ...blockBase,
    backgroundColor: qm.totalBlockBg,
    borderColor: qm.totalBlockBorder,
    padding: theme.spacing.md,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 72,
    textAlign: 'center',
  };

  const totalLabelStyle: React.CSSProperties = {
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.sm,
    color: theme.palette.text.white,
    margin: 0,
  };

  const totalValueStyle: React.CSSProperties = {
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize['2xl'],
    fontWeight: theme.typography.fontWeight.bold,
    color: theme.palette.text.white,
    margin: 0,
    marginTop: 4,
  };

  const finalExamBlockStyle: React.CSSProperties = {
    ...blockBase,
    backgroundColor: qm.finalExamBg,
    borderColor: qm.finalExamBorder,
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing.sm,
  };

  const finalExamLabelStyle: React.CSSProperties = {
    ...rowLabelStyle,
    color: qm.finalExamText,
    cursor: 'pointer',
    display: 'block',
  };

  const finalExamSubtitleStyle: React.CSSProperties = {
    ...rowSubtitleStyle,
    color: qm.finalExamSubtitle,
  };

  const iconBoxSize = 40;
  const searchIconOffset = 20;
  const searchResultsMaxHeight = 280;
  const checkboxSize = 16;

  const searchWrapStyle: React.CSSProperties = {
    position: 'relative',
    width: '100%',
  };

  const searchIconStyle: React.CSSProperties = {
    position: 'absolute',
    left: theme.spacing.sm2,
    top: '50%',
    transform: 'translateY(-50%)',
    pointerEvents: 'none',
  };

  const searchInputStyle: React.CSSProperties = {
    width: '100%',
    boxSizing: 'border-box',
    height: iconBoxSize,
    padding: theme.spacing.sm2,
    paddingLeft: `calc(${theme.spacing.sm2} + ${searchIconOffset}px)`,
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.sm,
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.default,
    border: `1px solid ${theme.palette.border.main}`,
    borderRadius: theme.shape.borderRadius.sm,
    outline: 'none',
  };

  const questionCheckboxWrapStyle: React.CSSProperties = {
    width: checkboxSize,
    height: checkboxSize,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: theme.shape.borderRadius.xs,
    border: `1px solid ${theme.palette.border.input}`,
    backgroundColor: theme.palette.background.paper,
  };

  const questionCheckboxWrapSelectedStyle: React.CSSProperties = {
    ...questionCheckboxWrapStyle,
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.main,
  };

  const questionListStyle: React.CSSProperties = {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.sm,
  };

  const questionItemStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing.sm,
    padding: theme.spacing.sm2,
    backgroundColor: theme.palette.input.background,
    border: `1px solid ${theme.palette.border.main}`,
    borderRadius: theme.shape.borderRadius.sm2,
    cursor: 'pointer',
  };

  const questionItemSelectedStyle: React.CSSProperties = {
    ...questionItemStyle,
    backgroundColor: theme.palette.background.paper,
  };

  const difficultyTagStyle = (
    bg: string,
    color: string
  ): React.CSSProperties => ({
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.xs,
    fontWeight: theme.typography.fontWeight.medium,
    color,
    backgroundColor: bg,
    padding: '2px 8px',
    borderRadius: theme.shape.borderRadius.xs,
  });

  const selectedSectionStyle: React.CSSProperties = {
    border: '1px solid rgba(217, 186, 0, 0.4)',
    backgroundColor: 'rgba(254, 249, 195, 0.4)',
    borderRadius: theme.shape.borderRadius.sm,
    padding: theme.spacing.md,
    marginTop: theme.spacing.sm,
  };

  const clearAllButtonStyle: React.CSSProperties = {
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.sm,
    fontWeight: theme.typography.fontWeight.semibold,
    color: '#B91C1C',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: theme.spacing.xs,
    marginLeft: 'auto',
    display: 'block',
  };

  const paginationWrapStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.sm,
    marginTop: theme.spacing.sm,
    flexWrap: 'wrap',
  };

  const paginationButtonStyle: React.CSSProperties = {
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.sm,
    fontWeight: theme.typography.fontWeight.medium,
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.border.main}`,
    borderRadius: theme.shape.borderRadius.sm,
    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
    cursor: 'pointer',
  };

  const paginationButtonDisabledStyle: React.CSSProperties = {
    ...paginationButtonStyle,
    opacity: 0.5,
    cursor: 'not-allowed',
  };

  const footerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: theme.spacing.sm,
    padding: theme.spacing.lg,
    paddingTop: 0,
    flexShrink: 0,
  };

  const cancelButtonStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.base,
    fontWeight: theme.typography.fontWeight.semibold,
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.border.main}`,
    borderRadius: theme.shape.borderRadius.sm,
    cursor: 'pointer',
  };

  const confirmButtonStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.sm,
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.base,
    fontWeight: theme.typography.fontWeight.semibold,
    color: theme.palette.primary.contrastText,
    backgroundColor: theme.palette.primary.main,
    border: 'none',
    borderRadius: theme.shape.borderRadius.sm,
    cursor: 'pointer',
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="quiz-lesson-modal-title"
      style={overlayStyle}
      onKeyDown={handleKeyDown}
    >
      <style>
        {`.QuizLessonModal-modal::-webkit-scrollbar { display: none; }`}
      </style>
      <div
        className="QuizLessonModal-modal"
        style={modalStyle}
        onClick={e => e.stopPropagation()}
      >
        <header style={headerStyle}>
          <div style={headerLeftStyle}>
            <img
              src={iconQuizSmallModalCourses}
              alt=""
              width={24}
              height={24}
              style={{
                objectFit: 'contain',
                flexShrink: 0,
                filter: 'brightness(0)',
              }}
              aria-hidden
            />
            <h2 id="quiz-lesson-modal-title" style={titleStyle}>
              {t('courses.create.configure_quiz_lesson')}
            </h2>
          </div>
          <button
            type="button"
            style={closeButtonStyle}
            onClick={onClose}
            aria-label={t('courses.create.cancel')}
          >
            <img
              src={iconCloseModalCourses}
              alt=""
              width={20}
              height={20}
              style={{ objectFit: 'contain', filter: 'brightness(0)' }}
              aria-hidden
            />
          </button>
        </header>

        <div style={bodyStyle}>
          <div style={manualBlockStyle}>
            <div style={manualBlockTextWrapperStyle}>
              <p style={rowLabelStyle}>
                {t('courses.create.manual_question_selection')}
              </p>
              <p style={rowSubtitleStyle}>
                {t('courses.create.manual_question_selection_subtitle')}
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={manualQuestionSelection}
              style={toggleTrackStyle}
              onClick={() => setManualQuestionSelection(prev => !prev)}
            >
              <span style={toggleThumbStyle} />
            </button>
          </div>

          {manualQuestionSelection && (
            <>
              <p style={sectionTitleStyle}>
                {t('courses.create.select_questions_from_bank')}
              </p>
              <div style={searchWrapStyle}>
                <span style={searchIconStyle} aria-hidden>
                  <img
                    src={iconSearchStep3Courses}
                    alt=""
                    width={18}
                    height={18}
                  />
                </span>
                <input
                  type="search"
                  value={questionSearch}
                  onChange={e => setQuestionSearch(e.target.value)}
                  placeholder={t('courses.create.search_questions_placeholder')}
                  aria-label={t('courses.create.search_questions_placeholder')}
                  style={searchInputStyle}
                />
              </div>
              {useApi && apiLoading ? (
                <p style={rowSubtitleStyle}>
                  {t('questionBank.loading')}
                </p>
              ) : useApi && !apiLoading && apiTotal === 0 ? (
                <p style={rowSubtitleStyle}>
                  {t('courses.create.no_questions_in_bank')}
                </p>
              ) : !useApi && questionsForBank.length === 0 ? (
                <p style={rowSubtitleStyle}>
                  {t('courses.create.no_questions_in_bank')}
                </p>
              ) : (
                <>
                  <p
                    style={{ ...rowLabelStyle, marginBottom: theme.spacing.xs }}
                  >
                    {t('courses.create.available_questions')} (
                    {useApi ? apiTotal : filteredAvailableQuestions.length})
                  </p>
                  <ul
                    style={{
                      ...questionListStyle,
                      maxHeight: searchResultsMaxHeight,
                      overflowY: 'auto',
                      padding: theme.spacing.sm,
                    }}
                  >
                    {filteredAvailableQuestions.map(quest => {
                      const selected = selectedQuestionIds.includes(quest.id);
                      const difficultyColors: Record<
                        string,
                        { bg: string; color: string }
                      > = {
                        easy: { bg: '#B4E4BB', color: '#166534' },
                        medium: { bg: '#FEEA9A', color: '#B45309' },
                        hard: { bg: '#FCCDCB', color: '#B91C1C' },
                      };
                      const d = difficultyColors[quest.difficulty] ?? {
                        bg: '#E5E7EB',
                        color: '#374151',
                      };
                      return (
                        <li
                          key={quest.id}
                          style={
                            selected
                              ? questionItemSelectedStyle
                              : questionItemStyle
                          }
                          onClick={() => toggleQuestion(quest)}
                          onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              toggleQuestion(quest);
                            }
                          }}
                          role="button"
                          tabIndex={0}
                        >
                          <div
                            style={
                              selected
                                ? questionCheckboxWrapSelectedStyle
                                : questionCheckboxWrapStyle
                            }
                            aria-hidden
                          >
                            {selected && (
                              <img
                                src={iconCheckSmallStep3Courses}
                                alt=""
                                width={checkboxSize - 4}
                                height={checkboxSize - 4}
                              />
                            )}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: theme.spacing.sm,
                                flexWrap: 'wrap',
                                marginBottom: 4,
                              }}
                            >
                              <span style={difficultyTagStyle(d.bg, d.color)}>
                                {quest.difficulty}
                              </span>
                              <span
                                style={{
                                  fontFamily: theme.typography.fontFamily,
                                  fontSize: theme.typography.fontSize.xs,
                                  color: theme.palette.text.secondary,
                                }}
                              >
                                {quest.category}
                              </span>
                            </div>
                            <p
                              style={{
                                fontFamily: theme.typography.fontFamily,
                                fontSize: theme.typography.fontSize.sm,
                                color: theme.palette.text.primary,
                                margin: 0,
                              }}
                            >
                              {quest.text}
                            </p>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                  {useApi && apiTotal > 0 && (
                    <div style={paginationWrapStyle}>
                      <button
                        type="button"
                        style={
                          apiPageIndex <= 0 || apiLoading
                            ? paginationButtonDisabledStyle
                            : paginationButtonStyle
                        }
                        onClick={() =>
                          setApiPageIndex(p => Math.max(0, p - 1))
                        }
                        disabled={apiPageIndex <= 0 || apiLoading}
                        aria-label={t('questionBank.previous')}
                      >
                        {t('questionBank.previous')}
                      </button>
                      <span
                        style={{
                          fontFamily: theme.typography.fontFamily,
                          fontSize: theme.typography.fontSize.sm,
                          color: theme.palette.text.secondary,
                        }}
                      >
                        {t('questionBank.pagination_summary', {
                          count: filteredAvailableQuestions.length,
                          total: apiTotal,
                        })}
                      </span>
                      <button
                        type="button"
                        style={
                          apiPageIndex >=
                            Math.max(0, Math.ceil(apiTotal / PAGE_SIZE) - 1) ||
                          apiLoading
                            ? paginationButtonDisabledStyle
                            : paginationButtonStyle
                        }
                        onClick={() => setApiPageIndex(p => p + 1)}
                        disabled={
                          apiPageIndex >=
                            Math.max(0, Math.ceil(apiTotal / PAGE_SIZE) - 1) ||
                          apiLoading
                        }
                        aria-label={t('questionBank.next')}
                      >
                        {t('questionBank.next')}
                      </button>
                    </div>
                  )}
                </>
              )}

              <div style={selectedSectionStyle}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: theme.spacing.sm,
                  }}
                >
                  <p
                    style={{
                      ...rowLabelStyle,
                      margin: 0,
                    }}
                  >
                    {t('courses.create.selected_questions')} (
                    {selectedQuestions.length})
                  </p>
                  {selectedQuestions.length > 0 && (
                    <button
                      type="button"
                      style={clearAllButtonStyle}
                      onClick={clearAllSelected}
                    >
                      {t('courses.create.clear_all')}
                    </button>
                  )}
                </div>
                <ul style={questionListStyle}>
                  {selectedQuestions.map(quest => {
                    const difficultyColors: Record<
                      string,
                      { bg: string; color: string }
                    > = {
                      easy: { bg: '#B4E4BB', color: '#166534' },
                      medium: { bg: '#FEEA9A', color: '#B45309' },
                      hard: { bg: '#FCCDCB', color: '#B91C1C' },
                    };
                    const d = difficultyColors[quest.difficulty] ?? {
                      bg: '#E5E7EB',
                      color: '#374151',
                    };
                    return (
                      <li
                        key={quest.id}
                        style={{
                          ...questionItemSelectedStyle,
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: theme.spacing.sm,
                        }}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: theme.spacing.sm,
                              flexWrap: 'wrap',
                              marginBottom: 4,
                            }}
                          >
                            <span style={difficultyTagStyle(d.bg, d.color)}>
                              {quest.difficulty}
                            </span>
                            <span
                              style={{
                                fontFamily: theme.typography.fontFamily,
                                fontSize: theme.typography.fontSize.xs,
                                color: theme.palette.text.secondary,
                              }}
                            >
                              {quest.category}
                            </span>
                          </div>
                          <p
                            style={{
                              fontFamily: theme.typography.fontFamily,
                              fontSize: theme.typography.fontSize.sm,
                              color: theme.palette.text.primary,
                              margin: 0,
                            }}
                          >
                            {quest.text}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={e => {
                            e.stopPropagation();
                            removeSelected(quest.id);
                          }}
                          aria-label={t('courses.create.clear_all')}
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: theme.spacing.xs,
                            cursor: 'pointer',
                            color: '#B91C1C',
                            fontSize: 18,
                            lineHeight: 1,
                            flexShrink: 0,
                          }}
                        >
                          ×
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </>
          )}

          <p style={sectionTitleStyle}>
            {t('courses.create.quiz_recipe_builder')}
          </p>
          <div
            style={{
              backgroundColor: '#F9F5FF',
              borderRadius: theme.shape.borderRadius.sm,
              padding: theme.spacing.lg,
              marginTop: 0,
              marginBottom: theme.spacing.sm,
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: theme.spacing.sm,
              }}
            >
              <div style={difficultyRowStyle('#B4E4BB')}>
                <div style={circleStyle('#B4E4BB', '#166534')}>E</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ ...rowLabelStyle, color: '#166534' }}>
                    {t('courses.create.easy_questions')}
                  </p>
                  <p style={rowSubtitleStyle}>
                    {t('courses.create.easy_questions_subtitle')}
                  </p>
                </div>
                <input
                  type="number"
                  min={0}
                  value={easyCount}
                  onChange={e => setEasyCount(clampNum(Number(e.target.value)))}
                  readOnly={manualQuestionSelection}
                  style={{
                    ...numInputStyle,
                    ...(manualQuestionSelection && {
                      cursor: 'default',
                      opacity: 0.9,
                    }),
                  }}
                  aria-label={t('courses.create.easy_questions')}
                  aria-readonly={manualQuestionSelection}
                />
              </div>
              <div style={difficultyRowStyle('#FEEA9A')}>
                <div style={circleStyle('#FEEA9A', '#B45309')}>M</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ ...rowLabelStyle, color: '#B45309' }}>
                    {t('courses.create.medium_questions')}
                  </p>
                  <p style={rowSubtitleStyle}>
                    {t('courses.create.medium_questions_subtitle')}
                  </p>
                </div>
                <input
                  type="number"
                  min={0}
                  value={mediumCount}
                  onChange={e =>
                    setMediumCount(clampNum(Number(e.target.value)))
                  }
                  readOnly={manualQuestionSelection}
                  style={{
                    ...numInputStyle,
                    ...(manualQuestionSelection && {
                      cursor: 'default',
                      opacity: 0.9,
                    }),
                  }}
                  aria-label={t('courses.create.medium_questions')}
                  aria-readonly={manualQuestionSelection}
                />
              </div>
              <div style={difficultyRowStyle('#FCCDCB')}>
                <div style={circleStyle('#FCCDCB', '#B91C1C')}>H</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ ...rowLabelStyle, color: '#B91C1C' }}>
                    {t('courses.create.hard_questions')}
                  </p>
                  <p style={rowSubtitleStyle}>
                    {t('courses.create.hard_questions_subtitle')}
                  </p>
                </div>
                <input
                  type="number"
                  min={0}
                  value={hardCount}
                  onChange={e => setHardCount(clampNum(Number(e.target.value)))}
                  readOnly={manualQuestionSelection}
                  style={{
                    ...numInputStyle,
                    ...(manualQuestionSelection && {
                      cursor: 'default',
                      opacity: 0.9,
                    }),
                  }}
                  aria-label={t('courses.create.hard_questions')}
                  aria-readonly={manualQuestionSelection}
                />
              </div>
            </div>

            <div style={{ ...totalBlockStyle, marginTop: theme.spacing.sm }}>
              <p style={totalLabelStyle}>
                {t('courses.create.total_questions')}
              </p>
              <p style={totalValueStyle}>{total}</p>
            </div>
          </div>

          <div style={finalExamBlockStyle}>
            <label
              htmlFor="quiz-final-exam"
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: theme.spacing.sm,
                cursor: 'pointer',
                flex: 1,
                minWidth: 0,
              }}
            >
              <input
                type="checkbox"
                id="quiz-final-exam"
                checked={isFinalExam}
                onChange={e => setIsFinalExam(e.target.checked)}
                aria-describedby="quiz-final-exam-desc"
                style={{
                  position: 'absolute',
                  opacity: 0,
                  width: 0,
                  height: 0,
                  margin: 0,
                  padding: 0,
                }}
              />
              <div
                style={
                  isFinalExam
                    ? questionCheckboxWrapSelectedStyle
                    : questionCheckboxWrapStyle
                }
                aria-hidden
              >
                {isFinalExam && (
                  <img
                    src={iconCheckSmallStep3Courses}
                    alt=""
                    width={checkboxSize - 4}
                    height={checkboxSize - 4}
                  />
                )}
              </div>
              <div>
                <span style={finalExamLabelStyle}>
                  {t('courses.create.set_as_course_final_exam')}
                </span>
                <p id="quiz-final-exam-desc" style={finalExamSubtitleStyle}>
                  {t('courses.create.set_as_course_final_exam_subtitle')}
                </p>
              </div>
            </label>
          </div>
        </div>

        <footer style={footerStyle}>
          <button type="button" style={cancelButtonStyle} onClick={onClose}>
            {t('courses.create.cancel')}
          </button>
          <button
            type="button"
            style={confirmButtonStyle}
            onClick={handleConfirm}
          >
            <img
              src={iconCheckWhiteModalCourses}
              alt=""
              width={18}
              height={18}
              style={{ objectFit: 'contain', flexShrink: 0 }}
              aria-hidden
            />
            {t('courses.create.add_lesson')}
          </button>
        </footer>
      </div>
    </div>
  );
}
